import React, { FC } from 'react';

import { Tooltip } from '@mui/material';

import { cn } from '../Utils/cnUtil';
import { AlarmChipSize } from './AlarmChipSize';

/**
 * Severity bands. Written out in full because Tailwind only keeps classes it can find as complete
 * strings in the source. The `100` fills sit a step above the `50` fills of `AlarmStatusChip` and
 * `AlarmTypeChip`, so a severity reads as its own badge rather than as another status pill.
 */
const SEVERITY_CLASSES = {
  low: 'bg-sky-100 text-sky-800 ring-sky-200',
  medium: 'bg-amber-100 text-amber-900 ring-amber-200',
  high: 'bg-red-100 text-red-800 ring-red-200',
  none: 'bg-gray-50 text-gray-400 ring-gray-200',
};

const severityClass = (value: number): string => {
  if (value <= 3) return SEVERITY_CLASSES.low;
  if (value <= 6) return SEVERITY_CLASSES.medium;
  return SEVERITY_CLASSES.high;
};

/**
 * Numeric severity badge. Renders a muted placeholder when an alarm carries no severity, so a
 * column of them stays aligned instead of collapsing to an empty cell.
 */
const AlarmSeverityChip: FC<AlarmSeverityChipProps> = ({
  className,
  value,
  tooltip,
  size = 'sm',
}: AlarmSeverityChipProps) => {
  const hasValue = value !== null && value !== undefined;

  const chip = (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md px-1',
        'font-semibold leading-none tabular-nums ring-1 ring-inset',
        size === 'md' ? 'h-6 min-w-[1.5rem] text-sm' : 'h-5 min-w-[1.25rem] text-xs',
        hasValue ? severityClass(value) : SEVERITY_CLASSES.none,
        className
      )}
    >
      {hasValue ? value : '–'}
    </span>
  );

  if (!tooltip) return chip;

  return (
    <Tooltip title={tooltip} placement="top">
      {chip}
    </Tooltip>
  );
};

interface AlarmSeverityChipProps {
  className?: string;
  /** `null` renders the muted placeholder. */
  value: number | null;
  /** Hover text — the placeholder is meaningless without one. */
  tooltip?: string;
  size?: AlarmChipSize;
}

export default AlarmSeverityChip;
